/**
 * OnlineGiftCardSalesPage.tsx
 *
 * The page /patients/:id shows for the location's "Online Gift Card Sales"
 * placeholder (a system patient, purpose `gift_card_guest`) instead of the
 * patient record. It is not a person: gift cards bought online by buyers who
 * weren't signed in are paid on it, so their sales count in Payments and
 * reports. Booking, messaging, editing and the rest of the patient tabs mean
 * nothing here, so this lists those guest sales instead: buyer, recipient,
 * amount, where the card stands, and links to each card and its sale payment.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable';
import { Badge } from '@/components/ui/Badge';
import { PatientLink } from '@/components/ui/PatientLink';
import { useAuth } from '@/hooks/useAuth';
import { formatCurrency } from '@/lib/utils';
import { getErrorMessage } from '@/services/apiClient';
import { listGiftCards, type GiftCard } from '@/services/gift-cards.service';
import { PaymentDetailModal } from '@/pages/payments/PaymentDetailModal';
import GiftCardDetailModal from './GiftCardDetailModal';

const PER_PAGE = 25;

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString();
}

/** Where a guest's card stands now. */
function cardStatus(card: GiftCard): { label: string; variant: 'warning' | 'info' | 'neutral' | 'error' } {
  if (card.claimedPatientId || card.issuedToPatientId) return { label: 'Claimed', variant: 'info' };
  if (card.currentBalance <= 0 && (card.redeemedAmount ?? 0) > 0) return { label: 'Used up', variant: 'neutral' };
  if (card.currentBalance <= 0) return { label: 'Voided', variant: 'error' };
  return { label: 'Unclaimed', variant: 'warning' };
}

export function OnlineGiftCardSalesPage({ patientId }: { patientId: number }) {
  const queryClient = useQueryClient();
  const { user, hasPermission } = useAuth();
  const canManageGiftCards = (user?.userLevel ?? 0) > 3 || hasPermission(14);

  const [page, setPage] = useState(1);
  const [cardId, setCardId] = useState<number | null>(null);
  const [paymentId, setPaymentId] = useState<number | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['gift-cards', 'guest-sales', page],
    queryFn: () => listGiftCards({ guestSales: true, page, perPage: PER_PAGE, sortBy: 'purchaseDate', sortDir: 'desc' }),
  });

  const columns: DataTableColumn<GiftCard>[] = [
    {
      key: 'purchaseDate',
      header: 'Sold',
      render: (card) => <span className="text-slate-600">{formatDate(card.purchaseDate)}</span>,
    },
    {
      key: 'buyerName',
      header: 'Buyer',
      render: (card) => (
        <div className="text-sm">
          <div className="font-medium text-slate-800">{card.buyerName ?? '—'}</div>
          {card.buyerEmail ? <div className="text-xs text-slate-500">{card.buyerEmail}</div> : null}
        </div>
      ),
    },
    {
      key: 'recipientName',
      header: 'Recipient',
      render: (card) => {
        if (card.claimedPatientId) {
          const name = card.claimedPatientName ?? `Patient #${card.claimedPatientId}`;
          return (
            <PatientLink
              patientId={card.claimedPatientId}
              patientName={name}
              onClick={(e) => e.stopPropagation()}
              className="text-sm font-medium text-blue-600 hover:underline"
            >
              {name}
            </PatientLink>
          );
        }
        return (
          <div className="text-sm">
            <div className="font-medium text-slate-800">{card.sendToSelf ? 'The buyer' : (card.recipientName ?? '—')}</div>
            {card.recipientEmail ? <div className="text-xs text-slate-500">{card.recipientEmail}</div> : null}
          </div>
        );
      },
    },
    {
      key: 'serialNumber',
      header: 'Card',
      render: (card) => <span className="font-mono text-slate-700">{card.serialNumber}</span>,
    },
    {
      key: 'originalValue',
      header: 'Amount',
      align: 'right',
      render: (card) => <span className="text-slate-700">{formatCurrency(card.originalValue)}</span>,
    },
    {
      key: 'currentBalance',
      header: 'Balance',
      align: 'right',
      render: (card) => <span className="font-semibold text-slate-900">{formatCurrency(card.currentBalance)}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (card) => {
        const status = cardStatus(card);
        return <Badge variant={status.variant} dot>{status.label}</Badge>;
      },
    },
    {
      key: 'salePaymentId',
      header: 'Sale',
      render: (card) =>
        card.salePaymentId ? (
          <button
            type="button"
            className="text-xs font-medium text-blue-600 hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              setPaymentId(card.salePaymentId ?? null);
            }}
          >
            Payment #{card.salePaymentId}
          </button>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        ),
    },
  ];

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 space-y-4 p-6">
        <PageHeader
          variant="compact"
          title="Online Gift Card Sales"
          subtitle="Gift cards bought online by buyers who weren't signed in."
        />
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          This isn&apos;t a patient. It holds the payments for gift cards bought online by buyers who weren&apos;t
          signed in, so those sales show in Payments and reports. Each location has one. It can&apos;t be booked,
          messaged or edited, and it is left out of patient lists and searches. Each card belongs to its recipient
          once they claim it.
        </div>
        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            Could not load the guest gift card sales. {getErrorMessage(error, '')}
          </div>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        <DataTable<GiftCard>
          data={data?.data ?? []}
          columns={columns}
          keyField="giftCardId"
          loading={isLoading}
          onRowClick={(card) => setCardId(card.giftCardId)}
          page={data?.meta.page ?? page}
          pageSize={PER_PAGE}
          total={data?.meta.total ?? 0}
          onPageChange={setPage}
          emptyTitle="No guest gift card sales yet"
          emptyDescription="Gift cards bought online without signing in will be listed here."
        />
      </div>

      {cardId !== null && (
        <GiftCardDetailModal
          open
          giftCardId={cardId}
          readOnly={!canManageGiftCards}
          onClose={() => setCardId(null)}
          onChanged={() => void queryClient.invalidateQueries({ queryKey: ['gift-cards'] })}
        />
      )}

      {paymentId !== null && (
        <PaymentDetailModal
          paymentId={paymentId}
          patientId={patientId}
          patientName="Online Gift Card Sales"
          open
          onClose={() => setPaymentId(null)}
        />
      )}
    </div>
  );
}
